/*
 * Profile-wizard persistence — SELF subject, REAL account only.
 *
 * The self subject reads/writes the real Cognito profile attributes through
 * the existing seam (src/auth/cognitoProfile.ts). No seeded demo profile
 * ships anywhere (LOCKED no-fabricated-data posture): the wizard operates on
 * the signed-in account's stored values, or an empty form.
 *
 * Attribute homes:
 *  - given_name / family_name / birthdate (YYYY-MM-DD) / gender /
 *    phone_number (E.164) / address / locale  → Cognito standard attributes.
 *  - nationality, residence country, city, emergency contact, T-shirt size,
 *    the about-me (self-describe) text, dial-code selections and language →
 *    the SERVER participant profile (my-api GET/PUT /me/profile, V29
 *    participant_profiles; boardroom Tier B 2026-10-01). The consumer pool
 *    has no attribute for these, so they used to live ONLY in a per-device
 *    localStorage record (key mytrumin.profileExtras.v1.<sub>) — lost on a
 *    new device, which also left get-ready's profile latch unable to fire
 *    for a newly assigned ticket. (Language ALSO keeps writing the Cognito
 *    `locale` attribute, as before; the server copy lets my-api judge an
 *    event that requires it.)
 *
 * ONE-TIME IDEMPOTENT UPLOAD of the old device record (loadSelfDraft):
 *  - server row exists ⇒ the server WINS; the local key is deleted.
 *  - server row empty (version 0) AND a local record exists ⇒ PUT it at
 *    version 0, then delete the local key. A 409 (another device/tab wrote
 *    first) ⇒ re-read and let the server win — never upload over a non-empty
 *    server row.
 *  - any failure ⇒ the local key is KEPT (shown for this session) and the
 *    next load retries.
 * DEGRADE (deploy order — the FE may ship before my-api/V29): a 404 (route
 * absent) or 503 (profile_store_unavailable) on save writes the device-local
 * record exactly as before, so a save never fails for want of the store; the
 * next load uploads it once the store exists. No VITE_MY_API_BASE (local dev
 * / unit tests) ⇒ device-local only.
 *
 * FITNESS: the old record carried a `fitness` display mirror. It is NOT sent
 * to the server (fitness lives in settings questionnaire_answers, submitted
 * via the questionnaire-answer BFF) and is no longer kept locally either — the
 * wizard only shows the fitness step while it is UNANSWERED (mapFitnessSelf),
 * so a mirror of an answered level had nothing left to display.
 */
import {
  getProfile, updateProfile, type CognitoProfile,
} from '../../auth/cognitoProfile'
import { refreshSession } from '../../auth/cognitoAuth'
import { loadSession, storeSession, type Session } from '../../auth/session'
import {
  MyApiError, fetchIdentityVerificationLevel, fetchLegacyProfile, fetchMyProfile, myApiBase, putAccountPhone, saveMyProfile,
  type IdentityVerificationLevel, type LegacyProfile, type ServerProfile,
} from '../../data/myApi'
import { EMPTY_DRAFT, LANGUAGES, MONTHS, STEP_FIELDS, fromE164, nationalFromStored, phoneState, toE164, type WizardDraft } from './wizardModel'
import { canonicalCity, canonicalDialLabel, countryByIso } from './referenceData'
import { phoneViaApiEnabled } from '../../lib/phoneViaApiFlag'

const EXTRAS_KEY = (sub: string) => `mytrumin.profileExtras.v1.${sub}`

/** Display label ↔ BCP-47 code for the Cognito `locale` attribute. */
const LOCALE_BY_LABEL: Record<string, string> = {
  English: 'en', 'العربية': 'ar', 'Français': 'fr', 'Español': 'es',
  Deutsch: 'de', Italiano: 'it', 'Português': 'pt', '中文': 'zh',
}
const LABEL_BY_LOCALE: Record<string, string> = Object.fromEntries(
  Object.entries(LOCALE_BY_LABEL).map(([label, code]) => [code, label]),
)

interface Extras {
  nationality?: string
  natIso?: string
  resCountry?: string
  resIso?: string
  city?: string
  dial?: string
  selfText?: string
  ecName?: string
  ecDial?: string
  ecPhone?: string
  ecRel?: string
  shirt?: string
  /** Server-only today (the old device record never carried it — language
   *  was Cognito `locale` only). Read from the server row as a fallback. */
  language?: string
}

/** Extras key ↔ server profile field (the ONE mapping, both directions). */
const EXTRAS_TO_SERVER: Array<[keyof Extras, keyof ServerProfile]> = [
  ['nationality', 'nationality'],
  ['natIso', 'nationalityIso'],
  ['resCountry', 'residenceCountry'],
  ['resIso', 'residenceIso'],
  ['city', 'city'],
  ['dial', 'phoneDial'],
  ['selfText', 'aboutText'],
  ['shirt', 'shirtSize'],
  ['language', 'language'],
  ['ecName', 'ecName'],
  ['ecDial', 'ecDial'],
  ['ecPhone', 'ecPhone'],
  ['ecRel', 'ecRelationship'],
]

/** The OLD device-local record (pre-V29). Read for the one-time upload and
 *  the deploy-order degrade only. A stray `fitness` key in it is ignored. */
function readExtras(sub: string): Extras {
  try {
    const raw = localStorage.getItem(EXTRAS_KEY(sub))
    return raw ? (JSON.parse(raw) as Extras) : {}
  } catch {
    return {}
  }
}

function writeExtras(sub: string, e: Extras): void {
  localStorage.setItem(EXTRAS_KEY(sub), JSON.stringify(e))
}

function clearExtras(sub: string): void {
  try {
    localStorage.removeItem(EXTRAS_KEY(sub))
  } catch {
    /* storage unavailable — nothing to clear */
  }
}

/** Upload body for the one-time migration: only the NON-EMPTY values (an
 *  insert leaves every absent column NULL). A device record from before the
 *  closed city list / server phone validation (CMT-2026-10-07-005) may hold a
 *  free-text city or an invalid emergency phone, which my-api now rejects with
 *  a 400 — and a rejected upload is retried on every load. Those values are
 *  left OUT of the upload (canonical city spelling otherwise), so the rest of
 *  the record lands and the user re-picks/re-enters the dropped field. */
export function extrasToServer(e: Extras): Partial<ServerProfile> {
  const out: Partial<ServerProfile> = {}
  for (const [k, f] of EXTRAS_TO_SERVER) {
    const v = e[k]
    if (typeof v === 'string' && v.trim() !== '') out[f] = v
  }
  if (out.city) {
    const canon = canonicalCity(e.resIso ? countryByIso(e.resIso) : null, out.city)
    if (canon) out.city = canon
    else delete out.city
  }
  if (out.ecPhone && !out.ecPhone.startsWith('+') && !phoneState(e.ecDial ?? '', out.ecPhone).ok) delete out.ecPhone
  return out
}

/** Fields of a device record the upload HELD BACK (would 400) — kept locally
 *  so the wizard prompts for them; never dropped or cleared (boardroom rule on
 *  #295). Only `city` / `ecPhone` can be held. */
function heldBack(local: Extras, upload: Partial<ServerProfile> = extrasToServer(local)): Extras {
  const held: Extras = {}
  // Each held value keeps the context it was judged against (residence / dial),
  // so a later load re-judges it identically.
  if (local.city?.trim() && !('city' in upload)) {
    held.city = local.city
    if (local.resIso) held.resIso = local.resIso
  }
  if (local.ecPhone?.trim() && !('ecPhone' in upload)) {
    held.ecPhone = local.ecPhone
    if (local.ecDial) held.ecDial = local.ecDial
  }
  return held
}

export function serverToExtras(p: ServerProfile): Extras {
  const out: Extras = {}
  for (const [k, f] of EXTRAS_TO_SERVER) {
    const v = p[f]
    if (typeof v === 'string' && v !== '') out[k] = v
  }
  return out
}

/** The save body: EVERY field from the draft ('' ⇒ null, an explicit clear),
 *  except city unless it is a list city (omitted — see below) and aboutText on a locked save (omitted ⇒ the server keeps the stored
 *  value — the identity-tied self-describe text is never re-written while
 *  locked, review P2-2). Language rides too (also written to Cognito). */
export function draftToServer(d: WizardDraft, identityLocked: boolean): Partial<ServerProfile> {
  const e: Required<Extras> = {
    nationality: d.nationality, natIso: d.natIso, resCountry: d.resCountry, resIso: d.resIso,
    city: d.city, dial: d.dial, selfText: d.selfText, shirt: d.shirt, language: d.language,
    ecName: d.ecName, ecDial: d.ecDial, ecPhone: d.ecPhone, ecRel: d.ecRel,
  }
  // CLOSED city list (CMT-2026-10-07-005): only a city in the residence
  // country's main-cities list is ever sent, in the list's canonical spelling.
  // Anything else — a legacy free-text value awaiting re-selection, or a field
  // the user is mid-way through re-typing — is OMITTED (never a null/clear):
  // the server keeps the stored value, so nothing is lost unless the person
  // picks a list city (boardroom rule on #295). The wizard's city gate blocks
  // Continue until they do.
  const city = canonicalCity(d.resIso ? countryByIso(d.resIso) : null, d.city)
  e.city = city ?? ''
  // A dial label with no calling code (Antarctica / Heard Island carry none
  // in the ISO dataset: "AQ +") is not a dial my-api accepts — send a clear.
  if (!/\d/.test(e.dial)) e.dial = ''
  if (!/\d/.test(e.ecDial)) e.ecDial = ''
  const out: Partial<ServerProfile> = {}
  for (const [k, f] of EXTRAS_TO_SERVER) {
    if (identityLocked && k === 'selfText') continue
    if (k === 'city' && !city) continue
    out[f] = e[k] ? e[k] : null
  }
  return out
}

/** Last server version seen per account (sub) — the optimistic-concurrency
 *  token for the next save. */
const serverVersion = new Map<string, number>()

/** Test seam: forget cached versions between cases. */
export function resetProfileStoreForTests(): void {
  serverVersion.clear()
}

/** Server row exists ⇒ the SERVER wins over the device record — except a
 *  value the one-time upload HELD BACK (a legacy city / phone the server would
 *  reject) that the server row does not hold: that stays on the device and in
 *  the draft, so the wizard prompts for it instead of losing it. */
function serverWins(sub: string, profile: ServerProfile, local: Extras): Extras {
  const fromServer = serverToExtras(profile)
  const held = heldBack(local)
  const keep: Extras = {}
  if (held.city && !fromServer.city) Object.assign(keep, { city: held.city, ...(held.resIso ? { resIso: held.resIso } : {}) })
  if (held.ecPhone && !fromServer.ecPhone) Object.assign(keep, { ecPhone: held.ecPhone, ...(held.ecDial ? { ecDial: held.ecDial } : {}) })
  if (Object.keys(keep).length > 0) writeExtras(sub, keep)
  else clearExtras(sub)
  // Only the held VALUES join the draft; residence / dial stay the server's.
  return { ...fromServer, ...(keep.city ? { city: keep.city } : {}), ...(keep.ecPhone ? { ecPhone: keep.ecPhone } : {}) }
}

/** The saved profile could NOT be read (GET /me/profile failed for a reason
 *  other than the deploy-order 404/503). The wizard must treat what it shows
 *  as NOT the saved profile and refuse to save over it — see loadExtras. */
export class ProfileLoadError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ProfileLoadError'
  }
}

/**
 * The account's extras: the SERVER profile, with the ONE-TIME IDEMPOTENT
 * UPLOAD of an old device record (see the file header).
 *
 * FAIL-CLOSED READ (prod incident 2026-10-08, Duncan's Apex Games profile):
 * this used to swallow EVERY fetchMyProfile failure and return the device
 * record — usually EMPTY on a new device — so a transient 500 / network error
 * rendered nationality / residence / city / emergency contact / shirt BLANK
 * with no error, and the final Save then PUT explicit nulls over the real
 * stored profile. Now only the DEPLOY-ORDER statuses degrade to the device
 * record — 404 (route absent) / 503 (profile_store_unavailable), the same two
 * persistExtras treats as degrade. "Server answered, no row" (200, profile
 * null) is NOT a failure: the one-time upload below handles it. Any other
 * failure (other non-2xx, network, unparseable body) THROWS ProfileLoadError,
 * so loadSelfDraft rejects and the wizard shows the honest load-failure state
 * with Save/Continue disabled. Failures AFTER a successful read (the upload,
 * its 409 re-read) still keep the device record and retry next load.
 */
async function loadExtras(session: Session, fetchImpl: typeof fetch): Promise<Extras> {
  const sub = session.claims.sub
  const local = readExtras(sub)
  const base = myApiBase()
  if (!base) return local // no my-api configured (dev/tests): device-local only
  let read
  try {
    read = await fetchMyProfile(session.idToken, base, fetchImpl)
  } catch (e) {
    // Route / store not deployed yet — keep the device record (deploy order).
    if (e instanceof MyApiError && (e.status === 404 || e.status === 503)) return local
    throw new ProfileLoadError(e instanceof Error ? e.message : 'profile fetch failed')
  }
  serverVersion.set(sub, read.version)
  if (read.profile) return serverWins(sub, read.profile, local)
  const upload = extrasToServer(local)
  if (Object.keys(upload).length === 0) {
    const held = heldBack(local, upload)
    if (Object.keys(held).length === 0) {
      clearExtras(sub) // nothing worth uploading (empty / fitness-only record)
      return {}
    }
    writeExtras(sub, held) // only held-back values: keep them for the prompt
    return local
  }
  try {
    const { version } = await saveMyProfile(session.idToken, base, upload, 0, fetchImpl)
    serverVersion.set(sub, version)
    // A held-back legacy city / phone is NOT dropped: the device record keeps
    // exactly those values, so the wizard keeps prompting until re-picked.
    const held = heldBack(local, upload)
    if (Object.keys(held).length > 0) writeExtras(sub, held)
    else clearExtras(sub)
    return local
  } catch (e) {
    if (e instanceof MyApiError && e.status === 409) {
      // Another device/tab wrote first: re-read and let the SERVER win —
      // never upload over a non-empty server row.
      try {
        const again = await fetchMyProfile(session.idToken, base, fetchImpl)
        serverVersion.set(sub, again.version)
        if (again.profile) return serverWins(sub, again.profile, local)
      } catch {
        /* fall through: keep the device record, retry next load */
      }
    }
    return local
  }
}

/** PUT the profile at the last-seen version (reading it first when unknown);
 *  a 409 re-reads the version and retries ONCE — an explicit Save is the
 *  user's reviewed intent, so it is applied over the newer row. */
async function putProfile(
  idToken: string,
  base: string,
  sub: string,
  fields: Partial<ServerProfile>,
  fetchImpl: typeof fetch,
): Promise<void> {
  let version = serverVersion.get(sub)
  if (version === undefined) version = (await fetchMyProfile(idToken, base, fetchImpl)).version
  try {
    const saved = await saveMyProfile(idToken, base, fields, version, fetchImpl)
    serverVersion.set(sub, saved.version)
  } catch (e) {
    if (!(e instanceof MyApiError) || e.status !== 409) throw e
    const fresh = await fetchMyProfile(idToken, base, fetchImpl)
    const saved = await saveMyProfile(idToken, base, fields, fresh.version, fetchImpl)
    serverVersion.set(sub, saved.version)
  }
}

/** "Is {city} your nearest city?" (my-api V35 cityFilledIn; Duncan ruling
 *  2026-10-07): the city to ask about when the server says the stored city was
 *  filled in by legacy data cleaning — else null. Null too when signed out,
 *  when no my-api is configured, or on ANY read failure (never prompt on a
 *  guess). Records the read version for the confirm. */
export async function loadNearestCityPrompt(fetchImpl: typeof fetch = fetch): Promise<string | null> {
  const session = loadSession()
  const base = myApiBase()
  if (!session || !base) return null
  try {
    const read = await fetchMyProfile(session.idToken, base, fetchImpl)
    serverVersion.set(session.claims.sub, read.version)
    const city = read.profile?.city?.trim()
    return read.profile?.cityFilledIn === true && city ? city : null
  } catch {
    return null
  }
}

/** The one-tap "Yes": PUT /me/profile with the SAME city at the read version
 *  (a 409 re-reads and retries once — putProfile). Any save carrying `city`
 *  clears the server flag. Throws on failure (the prompt stays, with an error). */
export async function confirmNearestCity(city: string, fetchImpl: typeof fetch = fetch): Promise<void> {
  const session = loadSession()
  const base = myApiBase()
  if (!session || !base) throw new Error('not signed in — nothing was saved')
  await putProfile(session.idToken, base, session.claims.sub, { city }, fetchImpl)
}

/** The residence country NAME for the draft: the stored name when there is
 *  one (never overridden), else the reference-list name for the stored ISO.
 *  my-api's legacy profile import (#299) writes residence_iso but not
 *  residence_country (my-api holds no country names), so without this an
 *  imported account would show an empty country despite a known ISO. An
 *  unknown ISO stays '' — a name is never invented. */
export function residenceName(stored: string | undefined, iso: string): string {
  if (stored) return stored
  return (iso && countryByIso(iso)?.n) || ''
}

function draftFromStores(p: CognitoProfile, extras: Extras): WizardDraft {
  const d: WizardDraft = { ...EMPTY_DRAFT }
  d.first = p.givenName ?? ''
  d.last = p.familyName ?? ''
  // birthdate YYYY-MM-DD → explicit Day / Month-name / Year selects
  if (p.birthdate && /^\d{4}-\d{2}-\d{2}$/.test(p.birthdate)) {
    const [y, m, day] = p.birthdate.split('-')
    d.dobY = String(Number(y))
    d.dobM = MONTHS[Number(m) - 1] ?? ''
    d.dobD = String(Number(day))
  }
  d.gender = p.gender ?? ''
  if (p.phone) {
    const parsed = fromE164(p.phone)
    if (parsed) {
      d.dial = parsed.dialLabel
      d.phone = parsed.national
    } else {
      d.phone = p.phone.replace(/^\+/, '') // unknown dial — surface digits honestly
    }
  }
  d.addr = p.address ?? ''
  d.language = p.locale ? (LABEL_BY_LOCALE[p.locale] ?? '') : ''
  // Cognito locale first (the established home); the server copy fills in
  // when the attribute is absent (only a label the wizard can render).
  if (!d.language && extras.language && LANGUAGES.includes(extras.language)) d.language = extras.language
  d.nationality = extras.nationality ?? ''
  d.natIso = extras.natIso ?? ''
  d.resIso = extras.resIso ?? ''
  d.resCountry = residenceName(extras.resCountry, d.resIso)
  d.city = extras.city ?? ''
  if (extras.dial) d.dial = canonicalDialLabel(extras.dial) // legacy `RU +73` → `RU +7`
  d.selfText = extras.selfText ?? ''
  d.ecName = extras.ecName ?? ''
  d.ecDial = canonicalDialLabel(extras.ecDial ?? '')
  // ec_phone is stored E.164 by my-api (CMT-2026-10-07-005); the wizard edits
  // national digits beside the ecDial select (no dial stored ⇒ derive it).
  if (extras.ecPhone?.startsWith('+') && !d.ecDial) d.ecDial = fromE164(extras.ecPhone)?.dialLabel ?? ''
  d.ecPhone = nationalFromStored(extras.ecPhone ?? '')
  d.ecRel = extras.ecRel ?? ''
  d.shirt = extras.shirt ?? ''
  // fitness: no mirror (see the file header) — '' = not answered in this
  // session; the wizard asks only while settings reports it unanswered.
  d.fitness = ''
  return d
}

/**
 * Map a legacy account's profile (from `GET /me/legacy-profile`, the CMT-035
 * marry-up) into a wizard draft for REVIEW-then-save. Mirrors draftFromStores
 * for the fields with the same home (name / DOB / gender / phone / address).
 *
 * Nationality and city are the one deliberate departure: the wizard persists
 * them ONLY when list-PICKED (canon §11.10 pick-only — natIso/resIso are what
 * make them valid), and a legacy free-text demonym/city can't be trusted to
 * match the reference list. So we seed them as the typeahead QUERY text (for
 * the user to confirm-pick) WITHOUT setting the iso codes — the gate still asks
 * the user to pick, which is exactly the "review your details" intent. Never
 * fabricates: a null legacy field maps to the empty draft value.
 */
export function draftFromLegacy(p: LegacyProfile): WizardDraft {
  const d: WizardDraft = { ...EMPTY_DRAFT }
  d.first = p.givenName ?? ''
  d.last = p.familyName ?? ''
  if (p.birthdate && /^\d{4}-\d{2}-\d{2}$/.test(p.birthdate)) {
    const [y, m, day] = p.birthdate.split('-')
    d.dobY = String(Number(y))
    d.dobM = MONTHS[Number(m) - 1] ?? ''
    d.dobD = String(Number(day))
  }
  d.gender = p.gender ?? ''
  if (p.phone) {
    const parsed = fromE164(p.phone)
    if (parsed) {
      d.dial = parsed.dialLabel
      d.phone = parsed.national
    } else {
      d.phone = p.phone.replace(/^\+/, '') // unknown dial — surface digits honestly
    }
  }
  d.addr = p.address ?? ''
  // Seed the typeahead query text only (no iso pick) — user confirms on review.
  d.nationality = p.nationality ?? ''
  d.city = p.city ?? ''
  return d
}

/**
 * Load the caller's LEGACY account profile into a wizard draft for review
 * (CMT-035 marry-up). Returns the pre-filled draft when the caller's email
 * matched a legacy account (server links it and returns the profile), else
 * null (no match / pending datalake lookup / no api base configured / no
 * session). The wizard falls back to the normal (empty or self) draft on null.
 */
export async function loadLegacyDraft(fetchImpl: typeof fetch = fetch): Promise<WizardDraft | null> {
  const session = loadSession()
  if (!session) return null
  const base = myApiBase()
  if (!base) return null
  const result = await fetchLegacyProfile(session.idToken, base, fetchImpl)
  if (!result.linked || !result.profile) return null
  return draftFromLegacy(result.profile)
}

/** Load the signed-in account's profile into a wizard draft: Cognito
 *  attributes + the SERVER participant profile (with the one-time upload of
 *  an old device record — loadExtras). Returns null when there is no session
 *  (the wizard renders honestly empty — routes are only reachable authed via
 *  App.tsx, but never assume). REJECTS (ProfileLoadError) when the server
 *  profile read failed for a non-deploy-order reason — the caller must not
 *  present an editable, saveable form over a profile it could not read
 *  (prod incident 2026-10-08). */
export async function loadSelfDraft(fetchImpl: typeof fetch = fetch): Promise<WizardDraft | null> {
  const session = loadSession()
  if (!session) return null
  const [profile, extras] = await Promise.all([
    getProfile(session.accessToken),
    loadExtras(session, fetchImpl),
  ])
  return draftFromStores(profile, extras)
}

/**
 * Load the account's identity-field verification level (LOCKED rule
 * PROGRAMME-RULE-IDENTITY-FIELD-EDIT-LOCK-BY-VERIFICATION-LEVEL-V1; dispatch
 * BOARDROOM-2026-08-29-114614). 3 = self-attested (free self-edit), 2 =
 * event-ID-verified (identity fields render read-only), 1 = third-party-
 * verified (same on this surface — no self-edit whenever level <= 2).
 *
 * FAIL-OPEN BY DESIGN: no session / no api base / any fetch failure ⇒ 3.
 * L3 is the rule's DEFAULT state (every profile starts there), so treating an
 * unknown level as 3 is the honest default — a read failure must never lock a
 * user out of editing their own profile. The lock is UX enforcement, not a
 * security boundary (see the saveSelfDraft note below), so failing open here
 * loses nothing that the server side was guaranteeing.
 */
export async function loadIdentityVerificationLevel(
  fetchImpl: typeof fetch = fetch,
): Promise<IdentityVerificationLevel> {
  try {
    const session = loadSession()
    if (!session) return 3
    const base = myApiBase()
    if (!base) return 3
    return await fetchIdentityVerificationLevel(session.idToken, base, fetchImpl)
  } catch {
    return 3 // fail-open: never lock self-edit on a read failure
  }
}

/** Persist the draft on Save (canon: Save profile). Cognito attributes via the
 *  existing seam; non-attribute fields to the device-local extras record.
 *
 *  `identityLocked` (level <= 2 — PROGRAMME-RULE-IDENTITY-FIELD-EDIT-LOCK-BY-
 *  VERIFICATION-LEVEL-V1): the identity-core attributes (given_name /
 *  family_name / birthdate / gender) are OMITTED from the Cognito update — the
 *  wizard renders them read-only, and this keeps the write honest with the
 *  render (a verified value is never re-written from this surface, even
 *  unchanged).
 *
 *  HONESTY NOTE (do not mistake this for enforcement): these writes go DIRECT
 *  from the browser to Cognito with the user's own access token, so everything
 *  here — the read-only fields AND this omission — is UX enforcement only, NOT
 *  a security boundary. A determined user can call UpdateUserAttributes
 *  themselves. REAL L2 enforcement needs either my-api-mediated profile writes
 *  or a Cognito pre-update trigger (infra-owned); that seam is a boardroom
 *  escalation, deliberately NOT built here (out of my-laptop's dispatch
 *  scope). */
export async function saveSelfDraft(
  d: WizardDraft,
  opts: { identityLocked?: boolean; fetchImpl?: typeof fetch; baseline?: WizardDraft | null } = {},
): Promise<void> {
  const session = loadSession()
  if (!session) throw new Error('not signed in — nothing was saved')
  const locked = opts.identityLocked === true
  await writeCognitoProfile(session, draftToCognito(d, locked), opts.fetchImpl)
  await refreshAfterCognitoWrite(session)
  // SERVER participant profile (V29) — written AFTER the refresh, with the
  // FRESH ID token: my-api's profile latch (run on this PUT) judges the
  // identity core from the token claims, so they must carry what was just
  // saved to Cognito.
  const fresh = loadSession() ?? session
  const sub = fresh.claims.sub
  // selfText (the gender-adjacent self-describe text) is IDENTITY-TIED — it
  // renders under the gender pills and locks with them — so a locked save
  // never re-writes it (review P2-2): omitted from the server body (the stored
  // value stands) and carried forward in the device-local degrade record.
  const writeLocal = () => {
    const storedSelfText = locked ? readExtras(sub).selfText : undefined
    writeExtras(sub, {
      nationality: d.nationality, natIso: d.natIso,
      resCountry: d.resCountry, resIso: d.resIso, city: d.city,
      dial: d.dial, selfText: locked ? storedSelfText : d.selfText,
      ecName: d.ecName, ecDial: d.ecDial, ecPhone: d.ecPhone, ecRel: d.ecRel,
      shirt: d.shirt,
    })
  }
  // SAVE ONLY WHAT CHANGED (prod incident 2026-10-08): the server body is the
  // diff against the persisted baseline, never the whole draft — see
  // changedServerFields. The device-local degrade record stays whole.
  await persistExtras(
    fresh,
    changedServerFields(draftToServer(d, locked), opts.baseline ? draftToServer(opts.baseline, locked) : null),
    writeLocal,
    opts.fetchImpl ?? fetch,
  )
}

/**
 * The final save's server body: ONLY the fields whose value differs from the
 * persisted baseline (both sides built by draftToServer, so the closed-city
 * omission, dial normalisation and the locked selfText omission apply
 * identically to each).
 *
 * WHY (prod incident 2026-10-08, Duncan's Apex Games profile): the full save
 * used to send EVERY extras field with '' ⇒ null. When the draft was blank
 * because the profile READ had failed, one successful PUT explicitly cleared
 * the stored nationality / residence / city / emergency contact / shirt. The
 * PUT is a PARTIAL update (absent key = unchanged — my-api parseProfilePut),
 * so omitting an unchanged field loses nothing.
 *  - baseline known: a field is sent when it differs. A value the user
 *    deliberately EMPTIED (baseline non-empty → '') still sends null — a real
 *    clear.
 *  - baseline null / unknown (legacy prefill under review, nothing loaded):
 *    NO null clears at all — only non-empty values are sent; nothing this
 *    session never read can be wiped by it.
 * An empty result is still PUT (version only): my-api runs the profile latch
 * on every PUT, as before. Pure.
 */
export function changedServerFields(
  full: Partial<ServerProfile>,
  baseline: Partial<ServerProfile> | null,
): Partial<ServerProfile> {
  const out: Partial<ServerProfile> = {}
  for (const f of Object.keys(full) as Array<keyof ServerProfile>) {
    const v = full[f]
    if (baseline === null) {
      if (v !== null && v !== undefined) out[f] = v
    } else if (v !== (baseline[f] ?? null)) {
      out[f] = v
    }
  }
  return out
}

/** The Cognito attribute set a save writes. `locked` (identity level <= 2)
 *  OMITS the identity core (given/family name, birthdate, gender) —
 *  profileToAttrs (cognitoProfile.ts) only writes DEFINED keys, so an omitted
 *  attribute never reaches Cognito. Phone/address/locale are written only
 *  when set (unchanged from the original saveSelfDraft). */
export function draftToCognito(d: WizardDraft, locked: boolean): CognitoProfile {
  const p: CognitoProfile = locked ? {} : {
    givenName: d.first,
    familyName: d.last,
  }
  if (!locked && d.dobD && d.dobM && d.dobY) {
    const m = MONTHS.indexOf(d.dobM) + 1
    p.birthdate = `${d.dobY.padStart(4, '0')}-${String(m).padStart(2, '0')}-${d.dobD.padStart(2, '0')}`
  }
  if (!locked && d.gender) p.gender = d.gender
  const e164 = toE164(d.dial || 'AE +971', d.phone)
  if (e164) p.phone = e164
  if (d.addr) p.address = d.addr
  if (d.language && LOCALE_BY_LABEL[d.language]) p.locale = LOCALE_BY_LABEL[d.language]
  return p
}

/** The ID token was minted at sign-in, BEFORE a save, so its
 *  given_name/family_name claims are stale (or absent for a first-time
 *  profile). Everything that reads the signed-in name from the token claims —
 *  notably the wallet ticket stub's Participant line (src/screens/Tickets.tsx)
 *  — would keep rendering a blank "—" until the next sign-in
 *  (BOARDROOM-2026-08-14-100859). Refresh the session so loadSession().claims
 *  carries the name just written. BEST-EFFORT: a refresh failure (no stored
 *  refresh token, or a transient Cognito error) must NOT fail the save — the
 *  attributes are already persisted and reconcile on the next sign-in;
 *  nothing is fabricated. */
/** The Cognito half of a save. With VITE_PHONE_VIA_API on, the phone goes
 *  to my-api PUT /me/phone (validated, written server-side as E.164) and every
 *  other attribute goes direct as before; a failed phone write throws, so the
 *  step shows the error and nothing else is written. Flag off, or no my-api
 *  base configured: today's single direct write. */
export async function writeCognitoProfile(
  session: Session,
  cognito: CognitoProfile,
  fetchImpl: typeof fetch = fetch,
  env: Record<string, string | undefined> = import.meta.env,
): Promise<void> {
  const base = myApiBase(env)
  if (cognito.phone !== undefined && phoneViaApiEnabled(env) && base) {
    const { phone, ...rest } = cognito
    await putAccountPhone(session.idToken, base, phone, fetchImpl)
    if (Object.keys(rest).length > 0) await updateProfile(session.accessToken, rest)
    return
  }
  await updateProfile(session.accessToken, cognito)
}

async function refreshAfterCognitoWrite(session: Session): Promise<void> {
  if (!session.refreshToken) return
  try {
    const username =
      session.claims['cognito:username'] || session.claims.email || session.claims.sub
    const tokens = await refreshSession(username, session.refreshToken)
    storeSession(tokens)
  } catch {
    /* keep the existing session; the saved name reconciles on next sign-in */
  }
}

/** Which wizard step OWNS each Cognito attribute (mirrors STEP_FIELDS). */
const COGNITO_STEP: Record<keyof CognitoProfile, number> = {
  email: 0, // never written by the wizard
  givenName: 1, familyName: 1, birthdate: 2, gender: 2, phone: 3, address: 3, locale: 5,
}

/** The writes a per-step save makes for `step`: the Cognito attributes and
 *  server-profile fields that step owns, built by the SAME mappers as the full
 *  save (draftToCognito / draftToServer), so the identity lock and the locked
 *  selfText omission hold exactly as on the final save. `local` is the same
 *  subset in device-record shape (deploy-order degrade). Pure. */
export function stepWrites(
  step: number,
  d: WizardDraft,
  locked: boolean,
): { cognito: CognitoProfile; server: Partial<ServerProfile>; local: Extras } {
  const owned = new Set<string>(STEP_FIELDS[step] ?? [])
  const cognito: CognitoProfile = {}
  const allCognito = draftToCognito(d, locked)
  for (const k of Object.keys(allCognito) as Array<keyof CognitoProfile>) {
    if (COGNITO_STEP[k] === step) cognito[k] = allCognito[k]
  }
  const allServer = draftToServer(d, locked)
  const server: Partial<ServerProfile> = {}
  const local: Extras = {}
  for (const [k, f] of EXTRAS_TO_SERVER) {
    if (!owned.has(k) || !(f in allServer)) continue
    local[k] = d[k]
    // P1 2026-10-22: a per-step save has no loaded baseline, so an EMPTY field
    // is never sent (it would become a clear of a value this draft may simply
    // not hold). Clearing is only via the final save's baseline diff.
    if (allServer[f] === null || allServer[f] === undefined) continue
    server[f] = allServer[f]
  }
  return { cognito, server, local }
}

/**
 * PER-STEP SAVE (quality defect 2 on #263): persist ONLY step `step`'s fields
 * when the participant leaves it via Continue, so progress is never lost and a
 * partially complete profile is stored.
 *  - identity/Cognito subset → Cognito (identity core omitted at level <= 2,
 *    exactly as saveSelfDraft); the session is refreshed after a Cognito
 *    write so the server PUT (and the token-claim readers) see fresh claims.
 *  - extras subset → PUT /me/profile with ONLY this step's keys. The server
 *    PUT is a PARTIAL update (absent key = unchanged — my-api
 *    participant-profile.ts parseProfilePut), so the merge with the CURRENT
 *    server row happens server-side under the optimistic version: saving step
 *    3 can never blank step 4's stored fields. 409 ⇒ re-read the version and
 *    retry ONCE (putProfile). 404/503 ⇒ the device-local degrade record,
 *    MERGED with what that record already holds.
 * Throws on a real failure — the wizard stays on the step with the error.
 */
export async function saveSelfStep(
  step: number,
  d: WizardDraft,
  opts: { identityLocked?: boolean; fetchImpl?: typeof fetch } = {},
): Promise<void> {
  const session = loadSession()
  if (!session) throw new Error('not signed in — nothing was saved')
  const { cognito, server, local } = stepWrites(step, d, opts.identityLocked === true)
  if (Object.keys(cognito).length > 0) {
    await writeCognitoProfile(session, cognito, opts.fetchImpl)
    await refreshAfterCognitoWrite(session)
  }
  if (Object.keys(server).length === 0) return
  const fresh = loadSession() ?? session
  const sub = fresh.claims.sub
  await persistExtras(fresh, server, () => writeExtras(sub, { ...readExtras(sub), ...local }), opts.fetchImpl ?? fetch)
}

/** Write extras `fields` (already lock-filtered by draftToServer) to the
 *  server profile; `writeLocal` is this save's device-local degrade write. */
async function persistExtras(
  session: Session,
  fields: Partial<ServerProfile>,
  writeLocal: () => void,
  fetchImpl: typeof fetch,
): Promise<void> {
  const sub = session.claims.sub
  const base = myApiBase()
  if (!base) {
    writeLocal() // no my-api configured (dev/tests): device-local only
    return
  }
  try {
    await putProfile(session.idToken, base, sub, fields, fetchImpl)
    // The server holds what this PUT carried; no stale device copy — EXCEPT a
    // held-back city / emergency phone this PUT did not carry (a legacy value
    // awaiting re-selection): that is never dropped (boardroom rule on #295).
    const prior = readExtras(sub)
    const keep = heldBack(prior)
    if (keep.city && 'city' in fields) { delete keep.city; delete keep.resIso }
    if (keep.ecPhone && 'ecPhone' in fields) { delete keep.ecPhone; delete keep.ecDial }
    if (keep.city || keep.ecPhone) writeExtras(sub, keep)
    else clearExtras(sub)
  } catch (e) {
    // DEPLOY-ORDER DEGRADE: route absent (404) or store not migrated (503) ⇒
    // keep the device-local record exactly as before; the next load uploads
    // it once the store exists. Anything else is a real save failure.
    if (e instanceof MyApiError && (e.status === 404 || e.status === 503)) {
      writeLocal()
      return
    }
    throw e
  }
}
